import { AdditiveBlending, BufferAttribute, BufferGeometry, Points, ShaderMaterial, Vector3 } from 'three';

export type ParticleKind = 'spark' | 'sweat' | 'puff';

interface Look {
  readonly colour: readonly [number, number, number];
  readonly size: readonly [number, number];
  readonly grow: number;
  readonly life: readonly [number, number];
  readonly speed: readonly [number, number];
  readonly gravity: number;
  readonly drag: number;
  readonly alpha: number;
}

// Colours above 1 glow through the bloom: sparks burn, sweat glints, a puff stays soft.
const looks: Record<ParticleKind, Look> = {
  spark: { colour: [4, 2.8, 1.2], size: [0.03, 0.055], grow: -0.5, life: [0.2, 0.42], speed: [2.2, 5.5], gravity: -7, drag: 3, alpha: 1 },
  sweat: { colour: [1.1, 1.3, 1.6], size: [0.02, 0.036], grow: 0, life: [0.45, 0.8], speed: [0.9, 2.6], gravity: -9.8, drag: 0.6, alpha: 0.85 },
  puff: { colour: [0.9, 0.85, 0.8], size: [0.06, 0.1], grow: 0.45, life: [0.3, 0.45], speed: [0.2, 0.7], gravity: 0.3, drag: 4, alpha: 0.28 },
};

const capacity = 480;
const scratch = new Vector3();

/**
 * Every spark, drop of sweat and puff of a blocked punch, in one draw: a fixed pool of points, each a soft round glow
 * that the shader sizes by distance. Dead points shrink to nothing and are reused.
 */
export class Particles {
  readonly points: Points;
  private readonly positions = new Float32Array(capacity * 3);
  private readonly tints = new Float32Array(capacity * 4);
  private readonly sizes = new Float32Array(capacity);
  private readonly velocities = new Float32Array(capacity * 3);
  private readonly ages = new Float32Array(capacity);
  private readonly lives = new Float32Array(capacity);
  private readonly startSizes = new Float32Array(capacity);
  private readonly kinds: Look[] = new Array(capacity).fill(looks.spark);
  private readonly material: ShaderMaterial;
  private next = 0;

  constructor() {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    geometry.setAttribute('tint', new BufferAttribute(this.tints, 4));
    geometry.setAttribute('size', new BufferAttribute(this.sizes, 1));
    this.material = new ShaderMaterial({
      uniforms: { viewHeight: { value: 1000 } },
      vertexShader: /* glsl */ `
        attribute vec4 tint;
        attribute float size;
        uniform float viewHeight;
        varying vec4 vTint;
        void main() {
          vTint = tint;
          vec4 view = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * view;
          gl_PointSize = size * projectionMatrix[1][1] * viewHeight * 0.5 / max(-view.z, 0.05);
        }`,
      fragmentShader: /* glsl */ `
        varying vec4 vTint;
        void main() {
          float d = length(gl_PointCoord - 0.5) * 2.0;
          float glow = smoothstep(1.0, 0.0, d);
          gl_FragColor = vec4(vTint.rgb, vTint.a * glow * glow);
        }`,
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    this.points = new Points(geometry, this.material);
    this.points.frustumCulled = false;
    this.points.renderOrder = 10;
  }

  /** The drawing buffer's height in pixels, so the points keep their size in metres. */
  setViewHeight(pixels: number): void {
    this.material.uniforms.viewHeight.value = pixels;
  }

  /** A burst of one kind at a point, flying mostly along a direction and spreading around it. */
  burst(kind: ParticleKind, at: Vector3, towards: Vector3, count: number, spread = 0.8): void {
    const look = looks[kind];
    for (let n = 0; n < count; n++) {
      const i = this.next;
      this.next = (this.next + 1) % capacity;
      scratch.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize().multiplyScalar(spread);
      scratch.add(towards).normalize().multiplyScalar(between(look.speed));
      this.positions.set([at.x, at.y, at.z], i * 3);
      this.velocities.set([scratch.x, scratch.y, scratch.z], i * 3);
      this.ages[i] = 0;
      this.lives[i] = between(look.life);
      this.startSizes[i] = between(look.size);
      this.kinds[i] = look;
    }
  }

  update(seconds: number): void {
    for (let i = 0; i < capacity; i++) {
      const life = this.lives[i];
      if (life <= 0) continue;
      const age = (this.ages[i] += seconds);
      const look = this.kinds[i];
      if (age >= life) {
        this.lives[i] = 0;
        this.sizes[i] = 0;
        continue;
      }
      const p = i * 3;
      const slow = Math.exp(-look.drag * seconds);
      this.velocities[p] *= slow;
      this.velocities[p + 1] = this.velocities[p + 1] * slow + look.gravity * seconds;
      this.velocities[p + 2] *= slow;
      this.positions[p] += this.velocities[p] * seconds;
      this.positions[p + 1] += this.velocities[p + 1] * seconds;
      this.positions[p + 2] += this.velocities[p + 2] * seconds;
      const done = age / life;
      this.sizes[i] = Math.max(0, this.startSizes[i] * (1 + look.grow * done * 3));
      const t = i * 4;
      this.tints[t] = look.colour[0];
      this.tints[t + 1] = look.colour[1];
      this.tints[t + 2] = look.colour[2];
      this.tints[t + 3] = look.alpha * (1 - done) * (1 - done);
    }
    const geometry = this.points.geometry;
    geometry.attributes.position.needsUpdate = true;
    geometry.attributes.tint.needsUpdate = true;
    geometry.attributes.size.needsUpdate = true;
  }
}

function between([low, high]: readonly [number, number]): number {
  return low + Math.random() * (high - low);
}
